// @ts-nocheck
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { PlusCircle, Edit, Trash2, Search, CheckCircle, XCircle, Calendar, MapPin, ExternalLink, Upload, Image } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import PaginationComponent from "@/components/common/PaginationComponent";
import { uploadCampLogo, ensureStorageBuckets } from "@/lib/storage-utils";
import { useSportsByGender } from "@/hooks/useSportsByGender";
import CampsCsvUpload from "./CampsCsvUpload";
import { getSessionUser } from '@/lib/authSession';

interface College {
  id: string;
  name: string;
  city?: string;
  state?: string;
}

interface Profile {
  id: string;
  name: string;
  email?: string;
}

interface CollegeCamp {
  id: string;
  college_id?: string;
  college_name?: string;
  division?: string;
  created_by?: string;
  title: string;
  location?: string;
  start_date?: string;
  end_date?: string;
  description?: string;
  website_url?: string;
  sport?: string;
  gender?: string;
  logo_url?: string;
  logo_file_url?: string;
  is_approved?: boolean;
  approval_status?: string;
  created_at?: string;
  college?: College;
  creator?: Profile;
}

const APPROVAL_STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' }
];

const DIVISION_OPTIONS = [
  { value: 'D1', label: 'D1' },
  { value: 'D2', label: 'D2' },
  { value: 'D3', label: 'D3' },
  { value: 'NAIA', label: 'NAIA' },
  { value: 'JUCO', label: 'JUCO' },
  { value: 'Other', label: 'Other' }
];

const CampAdminEditor: React.FC = () => {
  const [camps, setCamps] = useState<CollegeCamp[]>([]);
  const [colleges, setColleges] = useState<College[]>([]);
  const [totalCamps, setTotalCamps] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCamp, setEditingCamp] = useState<CollegeCamp | null>(null);
  
  const [formData, setFormData] = useState<Partial<CollegeCamp>>({
    title: "",
    location: "",
    start_date: "",
    end_date: "",
    description: "",
    website_url: "",
    college_name: "",
    division: "",
    gender: "",
    sport: "",
    logo_url: "",
    logo_file_url: ""
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const { toast } = useToast();
  
  // Sports hook for gender-based sport filtering
  const { sports } = useSportsByGender(formData.gender);

  useEffect(() => {
    console.log("🏕️ Camp Admin: Component mounted");
    ensureStorageBuckets();
    fetchColleges();
    fetchCamps();
  }, [currentPage, searchQuery, statusFilter]);

  const fetchColleges = async () => {
    try {
      console.log("🏕️ Camp Admin: Fetching colleges...");
      const { data, error } = await supabase
        .from('colleges')
        .select('id, name, city, state')
        .order('name');
      
      if (error) {
        console.error('🏕️ Camp Admin: Error fetching colleges:', error);
        throw error;
      }
      
      console.log("🏕️ Camp Admin: Loaded", data?.length || 0, "colleges");
      setColleges(data || []);
    } catch (error) {
      console.error('🏕️ Camp Admin: Error fetching colleges:', error);
      toast({
        title: 'Error',
        description: 'Failed to load colleges',
        variant: 'destructive'
      });
    }
  };

  const fetchCamps = async () => {
    setLoading(true);
    try {
      console.log("🏕️ Camp Admin: Fetching camps - page:", currentPage, "search:", searchQuery, "status:", statusFilter);
      
      // Check current user session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) {
        console.error("🏕️ Camp Admin: Session error:", sessionError);
      }
      
      console.log("🏕️ Camp Admin: Current session:", session ? "Authenticated" : "Not authenticated");
      
      // First fetch camps without joins to avoid foreign key conflicts
      const from = (currentPage - 1) * itemsPerPage;
      const to = from + itemsPerPage - 1;
      
      let campQuery = supabase
        .from('college_camps')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false });

      // Apply search filter
      if (searchQuery) {
        campQuery = campQuery.or(`title.ilike.%${searchQuery}%,location.ilike.%${searchQuery}%`);
      }

      // Apply status filter
      if (statusFilter !== 'all') {
        campQuery = campQuery.eq('approval_status', statusFilter);
      }

      // Apply pagination
      campQuery = campQuery.range(from, to);

      console.log("🏕️ Camp Admin: Executing camps query...");
      const { data: campsData, error: campsError, count } = await campQuery;
      
      if (campsError) {
        console.error("🏕️ Camp Admin: Camps query error:", campsError);
        throw campsError;
      }
      
      console.log("🏕️ Camp Admin: Query successful, loaded", campsData?.length || 0, "camps");
      
      // Now fetch related data separately
      const enrichedCamps = await Promise.all((campsData || []).map(async (camp) => {
        const enrichedCamp: CollegeCamp = { ...camp };
        
        // Fetch college data if college_id exists
        if (camp.college_id) {
          try {
            const { data: collegeData } = await supabase
              .from('colleges')
              .select('id, name, city, state')
              .eq('id', camp.college_id)
              .single();
            
            if (collegeData) {
              enrichedCamp.college = collegeData;
            }
          } catch (error) {
            console.warn("🏕️ Camp Admin: Could not fetch college for camp:", camp.id, error);
          }
        }
        
        // Fetch creator data if created_by exists
        if (camp.created_by) {
          try {
            const { data: creatorData } = await supabase
              .from('profiles')
              .select('id, name, email')
              .eq('user_id', camp.created_by)
              .single();
            
            if (creatorData) {
              enrichedCamp.creator = creatorData;
            }
          } catch (error) {
            console.warn("🏕️ Camp Admin: Could not fetch creator for camp:", camp.id, error);
          }
        }
        
        return enrichedCamp;
      }));
      
      setCamps(enrichedCamps);
      setTotalCamps(count || 0);
      
    } catch (error) {
      console.error('🏕️ Camp Admin: Error fetching camps:', error);
      toast({
        title: 'Error',
        description: `Failed to load camps: ${error.message || 'Unknown error'}`,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.title?.trim()) {
      errors.title = 'Camp title is required';
    }

    if (!formData.college_name?.trim()) {
      errors.college_name = 'College name is required';
    }

    if (!formData.division?.trim()) {
      errors.division = 'Division is required';
    }

    if (!formData.start_date) {
      errors.start_date = 'Start date is required';
    }

    if (formData.end_date && formData.start_date && formData.end_date < formData.start_date) {
      errors.end_date = 'End date cannot be before start date';
    }

    if (formData.website_url && !formData.website_url.match(/^https?:\/\/.+/)) {
      errors.website_url = 'Website must be a valid URL (starting with http:// or https://)';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetForm = () => {
    setFormData({
      title: "",
      location: "",
      start_date: "",
      end_date: "",
      description: "",
      website_url: "",
      college_name: "",
      division: "",
      gender: "",
      sport: "",
      logo_url: "",
      logo_file_url: ""
    });
    setFormErrors({});
    setEditingCamp(null);
    setLogoFile(null);
    setLogoPreview(null);
  };

  const handleEdit = (camp: CollegeCamp) => {
    console.log("🏕️ Camp Admin: Editing camp:", camp);
    setEditingCamp(camp);
    setFormData({
      title: camp.title || "",
      location: camp.location || "",
      start_date: camp.start_date || "",
      end_date: camp.end_date || "",
      description: camp.description || "",
      website_url: camp.website_url || "",
      college_name: camp.college_name || "",
      division: camp.division || "",
      gender: camp.gender || "",
      sport: camp.sport || "",
      logo_url: camp.logo_url || "",
      logo_file_url: camp.logo_file_url || ""
    });
    setFormErrors({});
    setLogoFile(null);
    setLogoPreview(camp.logo_file_url || camp.logo_url || null);
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      // Get current user
      const { data: { user }, error: userError } = await getSessionUser();
      if (userError) {
        console.error("🏕️ Camp Admin: User error:", userError);
        throw new Error('Authentication required');
      }
      
      if (!user) {
        throw new Error('No authenticated user found');
      }

      // Get current user's profile ID for created_by field
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();
        
      if (profileError || !profileData) {
        console.error("🏕️ Camp Admin: Profile error:", profileError);
        throw new Error('User profile not found');
      }

      // Handle logo upload if file is provided
      let logoFileUrl = formData.logo_file_url;
      if (logoFile) {
        const uploadedUrl = await uploadCampLogo(logoFile, editingCamp?.id || `temp-${Date.now()}`);
        if (uploadedUrl) {
          logoFileUrl = uploadedUrl;
        }
      }

      // Prepare camp data with correct field names matching database schema
      const campData = {
        title: formData.title?.trim(),
        location: formData.location?.trim(),
        start_date: formData.start_date,
        end_date: formData.end_date,
        description: formData.description?.trim(),
        website_url: formData.website_url?.trim(),
        college_name: formData.college_name?.trim(),
        division: formData.division?.trim(),
        gender: formData.gender?.trim(),
        sport: formData.sport?.trim(),
        logo_url: formData.logo_url?.trim(),
        logo_file_url: logoFileUrl?.trim(),
        approval_status: editingCamp ? editingCamp.approval_status : 'approved',
        is_approved: editingCamp ? editingCamp.is_approved : true
      };

      console.log("🏕️ Camp Admin: Submitting camp data:", campData);

      if (editingCamp) {
        console.log("🏕️ Camp Admin: Updating camp:", editingCamp.id);
        
        const { error } = await supabase
          .from('college_camps')
          .update(campData)
          .eq('id', editingCamp.id);
          
        if (error) {
          console.error("🏕️ Camp Admin: Update error:", error);
          console.error("🏕️ Camp Admin: Error details:", {
            message: error.message,
            details: error.details,
            hint: error.hint,
            code: error.code
          });
          throw error;
        }
        
        toast({
          title: 'Success',
          description: 'Camp updated successfully'
        });
      } else {
        console.log("🏕️ Camp Admin: Creating new camp");
        
        const insertData = {
          ...campData,
          created_by: profileData.id
        };

        console.log("🏕️ Camp Admin: Insert data:", insertData);
        
        const { error } = await supabase
          .from('college_camps')
          .insert([insertData]);
          
        if (error) {
          console.error("🏕️ Camp Admin: Insert error:", error);
          console.error("🏕️ Camp Admin: Error details:", {
            message: error.message,
            details: error.details,
            hint: error.hint,
            code: error.code
          });
          throw error;
        }
        
        toast({
          title: 'Success',
          description: 'Camp created successfully'
        });
      }
      
      setIsModalOpen(false);
      resetForm();
      fetchCamps();
      
    } catch (error) {
      console.error('🏕️ Camp Admin: Error saving camp:', error);
      toast({
        title: 'Error',
        description: `Failed to save camp: ${error.message || 'Unknown error'}`,
        variant: 'destructive'
      });
    }
  };

  const handleApprovalChange = async (campId: string, newStatus: string) => {
    try {
      console.log("🏕️ Camp Admin: Changing approval status:", campId, "to", newStatus);
      
      const { error } = await supabase
        .from('college_camps')
        .update({ 
          approval_status: newStatus,
          is_approved: newStatus === 'approved'
        })
        .eq('id', campId);
        
      if (error) {
        console.error("🏕️ Camp Admin: Approval update error:", error);
        throw error;
      }
      
      toast({
        title: 'Success',
        description: `Camp ${newStatus} successfully`
      });
      
      fetchCamps();
      
    } catch (error) {
      console.error('🏕️ Camp Admin: Error updating approval status:', error);
      toast({
        title: 'Error',
        description: `Failed to update camp status: ${error.message || 'Unknown error'}`,
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (id: string, title: string) => {
    try {
      console.log("🏕️ Camp Admin: Deleting camp:", { id, title });
      
      const { error } = await supabase
        .from('college_camps')
        .delete()
        .eq('id', id);
        
      if (error) {
        console.error("🏕️ Camp Admin: Delete error:", error);
        throw error;
      }
      
      toast({
        title: 'Success',
        description: 'Camp deleted successfully'
      });
      
      fetchCamps();
      
    } catch (error) {
      console.error('🏕️ Camp Admin: Error deleting camp:', error);
      toast({
        title: 'Error',
        description: `Failed to delete camp: ${error.message || 'Unknown error'}`,
        variant: 'destructive'
      });
    }
  };

  const getStatusBadge = (status?: string) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-800">Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800">Rejected</Badge>;
      case 'pending':
      default:
        return <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>;
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateRange = (startDate?: string, endDate?: string) => {
    if (!startDate) return '-';
    if (!endDate) return formatDate(startDate);
    
    const start = formatDate(startDate);
    const end = formatDate(endDate);
    
    if (start === end) return start;
    return `${start} - ${end}`;
  };

  const getCreatorDisplay = (camp: CollegeCamp) => {
    if (camp.creator?.name) {
      return camp.creator.name;
    }
    if (camp.creator?.email) {
      return camp.creator.email;
    }
    if (camp.created_by) {
      return `User ID: ${camp.created_by.substring(0, 8)}...`;
    }
    return 'Unknown';
  };

  const getCollegeDisplay = (camp: CollegeCamp) => {
    // First check joined college data
    if (camp.college?.name) {
      return camp.college.name;
    }
    // Fallback to college_name text field (used by CSV imports)
    if (camp.college_name) {
      return camp.college_name;
    }
    // Show truncated ID if only college_id exists
    if (camp.college_id) {
      return `College ID: ${camp.college_id.substring(0, 8)}...`;
    }
    return 'No College';
  };

  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGenderChange = (value: string) => {
    setFormData({
      ...formData, 
      gender: value,
      sport: "" // Reset sport when gender changes
    });
  };

  const totalPages = Math.ceil(totalCamps / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalCamps);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-heading font-bold text-primary-700">College Camps Management</h2>
          <p className="text-gray-600">Manage college camps and their approval status</p>
        </div>
        
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => { resetForm(); setIsModalOpen(true); }}>
              <PlusCircle className="h-4 w-4 mr-2" />
              Add Camp
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingCamp ? 'Edit Camp' : 'Add New Camp'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="title">Camp Title *</Label>
                  <Input
                    id="title"
                    value={formData.title || ""}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="Enter camp title"
                    className={formErrors.title ? "border-red-500" : ""}
                  />
                  {formErrors.title && (
                    <p className="text-sm text-red-500 mt-1">{formErrors.title}</p>
                  )}
                </div>
                
                <div>
                  <Label htmlFor="college_name">College *</Label>
                  <Input
                    id="college_name"
                    value={formData.college_name || ""}
                    onChange={(e) => setFormData({...formData, college_name: e.target.value})}
                    placeholder="Enter college name"
                    className={formErrors.college_name ? "border-red-500" : ""}
                  />
                  {formErrors.college_name && (
                    <p className="text-sm text-red-500 mt-1">{formErrors.college_name}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="start_date">Start Date *</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={formData.start_date || ""}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    className={formErrors.start_date ? "border-red-500" : ""}
                  />
                  {formErrors.start_date && (
                    <p className="text-sm text-red-500 mt-1">{formErrors.start_date}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="end_date">End Date</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={formData.end_date || ""}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    className={formErrors.end_date ? "border-red-500" : ""}
                  />
                  {formErrors.end_date && (
                    <p className="text-sm text-red-500 mt-1">{formErrors.end_date}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    value={formData.location || ""}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    placeholder="Camp location"
                  />
                </div>

                 <div>
                   <Label htmlFor="website_url">Website URL</Label>
                   <Input
                     id="website_url"
                     value={formData.website_url || ""}
                     onChange={(e) => setFormData({...formData, website_url: e.target.value})}
                     placeholder="https://..."
                     className={formErrors.website_url ? "border-red-500" : ""}
                   />
                   {formErrors.website_url && (
                     <p className="text-sm text-red-500 mt-1">{formErrors.website_url}</p>
                   )}
                 </div>

                 <div>
                   <Label htmlFor="gender">Gender</Label>
                    <Select 
                      value={formData.gender || ""} 
                      onValueChange={handleGenderChange}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Men">Men</SelectItem>
                        <SelectItem value="Women">Women</SelectItem>
                        <SelectItem value="Any and All">Any and All</SelectItem>
                      </SelectContent>
                    </Select>
                 </div>

                 <div>
                   <Label htmlFor="sport">Sport</Label>
                   <Select 
                     value={formData.sport || ""} 
                     onValueChange={(value) => setFormData({...formData, sport: value})}
                   >
                     <SelectTrigger>
                       <SelectValue placeholder="Select sport" />
                     </SelectTrigger>
                     <SelectContent>
                       {/* Show current value if it doesn't match any option */}
                       {formData.sport && !sports.some(s => s.name === formData.sport) && (
                         <SelectItem key="current" value={formData.sport}>
                           {formData.sport}
                         </SelectItem>
                       )}
                       {sports.map(sport => (
                         <SelectItem key={sport.id} value={sport.name}>
                           {sport.name}
                         </SelectItem>
                       ))}
                     </SelectContent>
                   </Select>
                 </div>

                 <div>
                    <Label htmlFor="division">Division <span className="text-red-500">*</span></Label>
                    <Select 
                      value={formData.division || ""} 
                      onValueChange={(value) => setFormData({...formData, division: value})}
                    >
                      <SelectTrigger className={formErrors.division ? 'border-red-500' : ''}>
                        <SelectValue placeholder="Select division" />
                      </SelectTrigger>
                      <SelectContent>
                        {DIVISION_OPTIONS.map(division => (
                          <SelectItem key={division.value} value={division.value}>
                            {division.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {formErrors.division && (
                      <p className="text-xs text-red-500 mt-1">{formErrors.division}</p>
                    )}
                  </div>
               </div>

               <div className="grid grid-cols-2 gap-4">
                 <div>
                   <Label htmlFor="logo_url">Logo URL</Label>
                   <Input
                     id="logo_url"
                     value={formData.logo_url || ""}
                     onChange={(e) => setFormData({...formData, logo_url: e.target.value})}
                     placeholder="https://example.com/logo.png"
                   />
                   <p className="text-xs text-muted-foreground mt-1">
                     Enter a direct URL to a logo image
                   </p>
                 </div>

                 <div>
                   <Label htmlFor="logo_file">Upload Logo</Label>
                   <div className="space-y-2">
                     <Input
                       id="logo_file"
                       type="file"
                       accept="image/*"
                       onChange={handleLogoFileChange}
                       className="file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                     />
                     <p className="text-xs text-muted-foreground">
                       Upload a logo file (JPG, PNG, GIF, WebP)
                     </p>
                   </div>
                 </div>
               </div>

               {(logoPreview || formData.logo_url) && (
                 <div>
                   <Label>Logo Preview</Label>
                   <div className="mt-2 p-4 border rounded-lg bg-gray-50">
                     <img 
                       src={logoPreview || formData.logo_url} 
                       alt="Logo preview" 
                       className="max-w-32 max-h-32 object-contain"
                       onError={(e) => {
                         e.currentTarget.style.display = 'none';
                       }}
                     />
                   </div>
                 </div>
               )}

               <div>
                 <Label htmlFor="description">Description</Label>
                 <Textarea
                   id="description"
                   value={formData.description || ""}
                   onChange={(e) => setFormData({...formData, description: e.target.value})}
                   placeholder="Camp description..."
                   rows={3}
                 />
               </div>
               
               <div className="flex justify-end space-x-2 pt-4">
                 <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                   Cancel
                 </Button>
                 <Button type="submit">
                   {editingCamp ? 'Update Camp' : 'Create Camp'}
                 </Button>
               </div>
             </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* CSV Upload Section */}
      <CampsCsvUpload onUploadComplete={fetchCamps} />

      {/* Filters */}
      <div className="flex gap-4 items-end">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search camps..."
            className="pl-8"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
        
        <div className="min-w-[200px]">
          <Label htmlFor="status-filter">Status Filter</Label>
          <Select value={statusFilter} onValueChange={(value) => {
            setStatusFilter(value);
            setCurrentPage(1);
          }}>
            <SelectTrigger>
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              {APPROVAL_STATUS_OPTIONS.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Camps Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            College Camps ({totalCamps > 0 ? `${startIndex}–${endIndex} of ${totalCamps}` : '0'})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary mx-auto"></div>
              <p className="mt-2 text-sm text-gray-500">Loading camps...</p>
            </div>
          ) : camps.length === 0 ? (
            <div className="text-center py-8 border rounded-md">
              <p className="text-gray-500">
                {searchQuery || statusFilter !== 'all' ? 'No camps found matching your filters' : 'No camps found. Add your first camp!'}
              </p>
            </div>
          ) : (
            <>
              <Table>
                 <TableHeader>
                   <TableRow>
                     <TableHead>Camp Title</TableHead>
                     <TableHead>College</TableHead>
                     <TableHead>Sport</TableHead>
                     <TableHead>Date Range</TableHead>
                     <TableHead>Location</TableHead>
                     <TableHead>Status</TableHead>
                     <TableHead>Created By</TableHead>
                     <TableHead className="text-right">Actions</TableHead>
                   </TableRow>
                 </TableHeader>
                <TableBody>
                  {camps.map((camp) => (
                    <TableRow key={camp.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <span>{camp.title}</span>
                          {camp.website_url && (
                            <a 
                              href={camp.website_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          )}
                        </div>
                      </TableCell>
                       <TableCell>
                         {getCollegeDisplay(camp)}
                       </TableCell>
                       <TableCell>
                         {camp.sport || '-'}
                       </TableCell>
                       <TableCell>
                         <div className="flex items-center gap-1">
                           <Calendar className="h-4 w-4 text-gray-400" />
                           {formatDateRange(camp.start_date, camp.end_date)}
                         </div>
                       </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <MapPin className="h-4 w-4 text-gray-400" />
                          {camp.location || '-'}
                        </div>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(camp.approval_status)}
                      </TableCell>
                      <TableCell>
                        {getCreatorDisplay(camp)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {camp.approval_status === 'pending' && (
                            <>
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="text-green-600 hover:text-green-700"
                                onClick={() => handleApprovalChange(camp.id, 'approved')}
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleApprovalChange(camp.id, 'rejected')}
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          {camp.approval_status === 'approved' && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => handleApprovalChange(camp.id, 'rejected')}
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          )}
                          {camp.approval_status === 'rejected' && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="text-green-600 hover:text-green-700"
                              onClick={() => handleApprovalChange(camp.id, 'approved')}
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                          )}
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleEdit(camp)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Camp</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete "{camp.title}"? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction 
                                  onClick={() => handleDelete(camp.id, camp.title)}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-6 flex justify-center">
                  <PaginationComponent
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CampAdminEditor;
